import { useCallback, useState } from 'react'
import { useCreateMarketplaceListing, useCreateMarketplaceListingProps } from './useCreateMarketplaceListing'

import { MarketplaceListingDetailQuery_marketplaceListing } from '../../../api/__generated__/MarketplaceListingDetailQuery'
import { MarketplaceListingPropsQueryV2_marketplaceListingPropsV2_MarketplaceListingPropsV2 } from '../../../api/__generated__/MarketplaceListingPropsQueryV2'
import moment from 'moment'
import { uploadV2Media } from '../../../utils'
import { useHistory } from 'react-router'
import { useToasts } from '@vestaboard/installables'
import { useUpdateMarketplaceListing } from '../../../api'
import { useUpdateMarketplaceListingV2 } from './useUpdateMarketplaceListingV2'
import { useUploadMarketplaceListingIconV2 } from './useUploadMarketplaceListingIconV2'
import { useV2GraphqlMutationErrorHandler } from '../../../utils/errors'

export const useCloneMarketplaceListing = () => {
  const [create] = useCreateMarketplaceListing()
  const [createProps] = useCreateMarketplaceListingProps()
  const [update] = useUpdateMarketplaceListing()
  const [updateV2] = useUpdateMarketplaceListingV2()
  const graphqlErrorHandlerV2 = useV2GraphqlMutationErrorHandler()
  const [getIconUploadUrl] = useUploadMarketplaceListingIconV2()
  const { addToast } = useToasts()
  const history = useHistory()

  const [loading, setLoading] = useState(false)

  const clone = useCallback(
    async (
      listing: MarketplaceListingDetailQuery_marketplaceListing,
      props?: MarketplaceListingPropsQueryV2_marketplaceListingPropsV2_MarketplaceListingPropsV2 | null
    ) => {
      setLoading(true)
      try {
        const createdListing = await create({
          variables: {
            title: `${listing.title} (Clone)`,
            installable: listing.installable.id,
            description: listing.description || '',
            releaseDate: listing.releaseDate || null
          }
        })

        if (!createdListing.data?.createMarketplaceListing.id) {
          setLoading(false)
          return
        }

        if (props) {
          const { data: propsData, errors: propsErrors } = await createProps({
            variables: {
              input: {
                marketplaceListingId: createdListing.data?.createMarketplaceListing.id,
                propsJson: props.propsJson
              }
            }
          })
          const { success } = graphqlErrorHandlerV2(
            'createProps',
            propsData?.createMarketplaceListingPropsV2,
            propsErrors
          )
          if (!success) {
            setLoading(false)
            return
          }
        }

        const date = moment(parseInt(listing.releaseDate)).format('YYYY-MM-DD')
        const dateParts = date.split('-')

        await Promise.all([
          update({
            variables: {
              input: {
                id: createdListing.data?.createMarketplaceListing.id,
                title: `${listing.title} (Clone)`,
                description: listing.description || '',
                releaseMonth: parseInt(dateParts[1]),
                releaseYear: parseInt(dateParts[0]),
                releaseDay: parseInt(dateParts[2]),
                developer: listing.creator?.id ?? '',
                iconUrl: listing.iconMedia?.id ?? listing.icon,
                installable: listing.installable.id,
                messageSet: listing.messageSetId ?? null,
                configuration: JSON.stringify(listing?.configuration),
                categories: listing.categories.map(category => category.id),
                language: listing.language?.id,
                version: listing.version,
                carouselImage1: listing?.carouselMedia1,
                carouselImage2: listing?.carouselMedia2,
                carouselImage3: listing?.carouselMedia3,
                noteCarouselImage1: listing?.noteCarouselMedia1,
                noteCarouselImage2: listing?.noteCarouselMedia2,
                noteCarouselImage3: listing?.noteCarouselMedia3,
                shortDescription: listing.shortDescription || '',
                featured: listing?.isFeatured || false,
                stagingOnly: true,
                free: listing.free,
                questionAndAnswerId: listing.questionAndAnswerId || null,
                noteQuestionAndAnswerId: listing.noteQuestionAndAnswerId || null,
                supportsFlagship: listing.supportsFlagship,
                supportsNote: listing.supportsNote,
                tags: (listing?.tags || '')
                  .split(',')
                  .map(tag => tag.trim())
                  .join(','),
                previewMessage: listing.previewMessage,
                notePreviewMessage: listing.notePreviewMessage
              }
            }
          }),
          updateV2({
            variables: {
              input: {
                id: createdListing.data?.createMarketplaceListing.id,
                title: `${listing.title} (Clone)`,
                description: listing.description || '',
                installableId: listing.installable.id,
                releaseAt: moment(
                  `${dateParts[0]}-${String(dateParts[1]).padStart(2, '0')}-${String(dateParts[2]).padStart(2, '0')}`
                )
                  .startOf('day')
                  .valueOf(),
                developer: listing.creator?.id || '',
                categoryIds: listing.categories.map(category => category.id),
                configuration: JSON.stringify(listing.configuration),
                installDeepLink: `https://platform.vestaboard.com/deeplink/marketplace-listing/${createdListing.data?.createMarketplaceListing.id}/install/ref=deeplink`,
                isFree: listing.free,
                messageSetId: listing.messageSetId,
                versionNumber: listing?.version,
                supportsFlagship: listing.supportsFlagship,
                supportsNote: listing.supportsNote,
                shortDescription: listing.shortDescription,
                previewMessage: listing.previewMessage,
                notePreviewMessage: listing.notePreviewMessage,
                questionAndAnswerId: listing?.questionAndAnswerId,
                noteQuestionAndAnswerId: listing?.noteQuestionAndAnswerId,
                stagingOnly: true
              }
            }
          })
        ])

        const iconUrl = listing.icon

        if (iconUrl) {
          const iconResponse = await fetch(iconUrl)
          const iconBlob = await iconResponse.blob()
          const iconUploadUrl = await getIconUploadUrl({
            variables: {
              input: {
                marketplaceListingId: createdListing.data?.createMarketplaceListing.id
              }
            }
          })
          if (iconUploadUrl?.data?.uploadMarketplaceListingLogoV2?.__typename === 'MarketplaceListingUploadV2') {
            const uploadUrl = iconUploadUrl.data.uploadMarketplaceListingLogoV2.uploadUrl
            await uploadV2Media(iconBlob, uploadUrl)
          }
        }

        addToast('Marketplace listing cloned successfully', { appearance: 'success' })
        history.push(`/marketplace-listings/${createdListing.data?.createMarketplaceListing.id}`)
      } catch (error) {
        addToast('Error cloning marketplace listing', { appearance: 'error' })
      }
      setLoading(false)
    },
    [addToast, create, getIconUploadUrl, history, update, updateV2, createProps, graphqlErrorHandlerV2]
  )

  return [clone, { loading }] as const
}
